import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './AdminTrackVideos.module.css';
import { logger } from '../config';
import {
  fetchAdminContents,
  getToken,
  handleApiError,
} from '../utils/api';
import ContentsGrid from './AdminCategoryContents/ContentsGrid';
import { SuccessToast, useSuccessToast } from './shared/SuccessToast';
import {
  AdminCategoryContentsHeader,
  AdminCategoryContentsLoadingState,
  getAdminSessionInfo,
  sortContents,
} from './AdminCategoryContents/helpers';
import {
  CreateContentModal,
  DeleteContentModal,
  EditContentModal,
} from './AdminCategoryContents/modals';

const AdminCategoryContents = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryId = String(searchParams.get('categoryId') ?? '').trim();

  const [adminInfo, setAdminInfo] = useState({ email: '', fullName: '' });
  const [categoryName, setCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contents, setContents] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editContent, setEditContent] = useState(null);
  const [deleteContent, setDeleteContent] = useState(null);
  const { message: successMessage, isExiting: successMessageExiting, showToast: showSuccessMessage } = useSuccessToast();

  const normalizedContents = useMemo(() => sortContents(contents), [contents]);

  const refreshContents = useCallback(async () => {
    if (!categoryId) {
      setError('Missing categoryId. Please open this page from Categories.');
      setContents([]);
      setCategoryName('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        handleApiError(new Error('No token found'), navigate, true);
        return;
      }
      const result = await fetchAdminContents({ categoryId }, navigate);
      if (!result) {
        setError('Failed to load category Quick Richuels.');
        setContents([]);
        return;
      }
      setCategoryName(result.category || '');
      setContents(Array.isArray(result.contents) ? result.contents : []);
    } catch (e) {
      logger.error('Error loading category content:', e);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate, categoryId]);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      handleApiError(new Error('No token found'), navigate, true);
      return;
    }
    setAdminInfo(getAdminSessionInfo());
    refreshContents();
  }, [navigate, refreshContents]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/admin-login');
  };

  const handleCreated = useCallback((message) => {
    setCreateOpen(false);
    showSuccessMessage(message || 'Quick Richuel created successfully.');
    refreshContents();
  }, [refreshContents, showSuccessMessage]);

  const handleSaved = useCallback((message) => {
    setEditContent(null);
    showSuccessMessage(message || 'Quick Richuel updated successfully.');
    refreshContents();
  }, [refreshContents, showSuccessMessage]);

  const handleDeleted = useCallback((message) => {
    setDeleteContent(null);
    showSuccessMessage(message || 'Quick Richuel deleted successfully.');
    refreshContents();
  }, [refreshContents, showSuccessMessage]);

  return (
    <div className={styles['admin-track-videos-container']}>
      <AdminCategoryContentsHeader
        adminInfo={adminInfo}
        categoryName={categoryName}
        onBack={() => navigate('/admin-categories')}
        onLogout={handleLogout}
      />

      <main className={styles['admin-main-content']}>
        <SuccessToast message={successMessage} isExiting={successMessageExiting} />
        {error && <div className={styles['error-message']}>{error}</div>}

        <div className={styles['toolbar']}>
          {!loading && (
            <p className={styles['count']}>
              {normalizedContents.length} {normalizedContents.length === 1 ? 'Quick Richuel' : 'Quick Richuels'}
            </p>
          )}
          <button
            type="button"
            className={styles['create-button']}
            onClick={() => setCreateOpen(true)}
            disabled={!categoryId}
          >
            + Create Quick Richuel
          </button>
        </div>

        {loading ? (
          <AdminCategoryContentsLoadingState message="Loading category Quick Richuels…" />
        ) : normalizedContents.length === 0 ? (
          <div className={styles['empty-state']}>No data yet.</div>
        ) : (
          <ContentsGrid
            contents={normalizedContents}
            onEdit={setEditContent}
            onDelete={setDeleteContent}
          />
        )}
      </main>

      {createOpen && (
        <CreateContentModal
          categoryId={categoryId}
          navigate={navigate}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}
      {editContent && (
        <EditContentModal
          item={editContent}
          categoryId={categoryId}
          navigate={navigate}
          onClose={() => setEditContent(null)}
          onSaved={handleSaved}
        />
      )}
      {deleteContent && (
        <DeleteContentModal
          item={deleteContent}
          navigate={navigate}
          onClose={() => setDeleteContent(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};

export default AdminCategoryContents;
